import { t } from 'i18next';
import { BackButton } from '../../../common/components/BackButton';
import { ComponentLoader } from '../../../common/components/ComponentLoader';
import { BaseController, BaseState } from '../../../common/controllers/BaseController';
import { AuthorizationError } from '../../../common/errors/errors';
import { bind } from '../../../common/utils/decorators';
import { ErrorNotification, SuccessNotification } from '../../../common/utils/notifications/Notification';
import { ServiceNowIntegration } from '../../../integrations/service-now/models/ServiceNowIntegration';
import { CheckServiceNowConnectionArgs, ServiceNowRepository, UpdateServiceNowArgs } from '../../../integrations/service-now/services/ServiceNowRepository';
import { IntegrationsRepository } from '../../../integrations/services/IntegrationsRepository';
import { SecondaryNav } from '../../../layout/components/SecondaryNav';
import { ModalManager } from '../../../modal-flows/services/ModalManager';
import { PATHS, TabNameLocalizationKeys } from '../../../routing/routes';
import { FeatureFlagsType, getFeatureFlag } from '../../../config/launch-darkly/FeatureFlagsController';
import { ServiceNowAccordion } from '../components/ServiceNowAccordion';

type State = BaseState & {
  isInitialLoading: boolean;
  isLoading: boolean;
  model: ServiceNowIntegration | null;
};

type Props = {};

export class ServiceNowController extends BaseController<Props, State> {
  private readonly integrationsRepository = new IntegrationsRepository();
  private readonly repository = new ServiceNowRepository();
  private readonly modalManager = new ModalManager();

  constructor(props: Props, context: unknown) {
    super(props, context);

    this.state = {
      ...super.getBaseState(),
      isInitialLoading: true,
      isLoading: true,
      model: null,
    };
  }

  componentWillUnmount() {
    super.componentWillUnmount();
    this.integrationsRepository.cancelAllRequests();
  }

  protected shouldCheckPermissionToAccess(): boolean {
    return true;
  }

  protected async checkPermissionToAccess(): Promise<void> {
    const serviceNowEnabled = getFeatureFlag(FeatureFlagsType.ENABLE_SERVICE_NOW);
    if (!serviceNowEnabled) {
      throw new AuthorizationError(PATHS.ORGANIZATION_DASHBOARD);
    }
    const permission = await this.permissionsService.canUpdateServiceNow();
    if (!permission) {
      throw new AuthorizationError(PATHS.ORGANIZATION_DASHBOARD);
    }
    this.fetchData();
  }

  @bind
  async fetchData() {
    try {
      this.setState({ isLoading: true });
      const integrations = await this.integrationsRepository.getIntegrations();
      this.setState({ isInitialLoading: false, isLoading: false, model: integrations?.getServiceNow() ?? null });
    } catch (e: unknown) {
      this.setState({ isInitialLoading: false, isLoading: false, model: null });
      this.handleError(e);
    }
  }

  @bind
  async handleTestIntegration(args: CheckServiceNowConnectionArgs) {
    try {
      this.setState({ isLoading: true });
      await this.testConnection(args);
      this.setState({ isLoading: false });
    } catch (e: unknown) {
      this.setState({ isLoading: false });
      this.handleError(e);
    }
  }

  async testConnection(args: CheckServiceNowConnectionArgs) {
    const result = await this.repository.checkConnection(args);
    if (result.isSuccessful()) {
      this.showMessage(new SuccessNotification(t('integrationList.serviceNow.testSuccess')));
      return true;
    } else {
      const message = result.getReason() ?? t('integrationList.serviceNow.testFail');
      this.showMessage(new ErrorNotification(message));
      return false;
    }
  }

  @bind
  async handleUpdate(args: UpdateServiceNowArgs) {
    try {
      this.setState({ isLoading: true });
      const updatedModel = await this.repository.updateIntegration(args);
      this.showMessage(new SuccessNotification(t('integrationList.serviceNow.updateSuccess')));
      this.setState({ isLoading: false, model: updatedModel });
    } catch (e: unknown) {
      this.setState({ isLoading: false });
      this.handleError(e);
    }
  }

  @bind
  async handleUnlinkIntegration() {
    const { confirmUnlink } = await this.modalManager.unlinkIntegration('integrationList.serviceNow.title', null);
    if (!confirmUnlink) {
      return;
    }
    try {
      this.setState({ isLoading: true });
      await this.repository.unlinkIntegration();
      this.setState({ isLoading: false, model: null });
      this.showMessage(new SuccessNotification(t('integrationList.serviceNow.updateSuccess')));
    } catch (e: unknown) {
      this.setState({ isLoading: false });
      this.handleError(e);
    }
  }

  @bind
  private getBreadcrumbs() {
    return this.breadcrumbFactory.getIntegrationBreadcrumbs({
      integrationName: t(TabNameLocalizationKeys.SERVICE_NOW),
      integrationPath: PATHS.INTEGRATION_SERVICE_NOW,
    });
  }

  render() {
    const { isInitialLoading, isLoading, model } = this.state;
    return this.renderBase(
      <SecondaryNav title={t(TabNameLocalizationKeys.SERVICE_NOW)} subtitle={t('integrationList.serviceNow.subtitle')} getBreadcrumbs={this.getBreadcrumbs}>
        <BackButton sx={{ mb: 2 }} />
        <ComponentLoader loading={isLoading}>
          <ServiceNowAccordion
            canEdit={true}
            isLoading={isInitialLoading}
            model={model}
            onConnectClick={this.handleUpdate}
            onTestClick={this.handleTestIntegration}
            onUnlinkClick={this.handleUnlinkIntegration}
          />
        </ComponentLoader>
      </SecondaryNav>
    );
  }
}
